import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cable, Layers, LayoutGrid, PauseCircle, Search } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { buildDataMap, sourceKind } from '../../lib/dataMap';

// Channels, Schemas and Sources on one page, laid out the way they are wired:
// each channel lists the schemas it uses and the sources that feed it.
export function DataScreen() {
  const { channels, templates, mappings, isChannelOwner, isTemplateOwner, isMappingOwner, syncChannels, syncTemplates, syncMappings } = useStore();
  const [q, setQ] = useState('');
  useEffect(() => {
    void syncChannels();
    void syncTemplates();
    void syncMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const map = useMemo(
    () =>
      buildDataMap(
        channels.filter((c) => isChannelOwner(c)),
        templates.filter((t) => isTemplateOwner(t)),
        mappings.filter((m) => isMappingOwner(m)),
      ),
    [channels, templates, mappings, isChannelOwner, isTemplateOwner, isMappingOwner],
  );
  const needle = q.trim().toLowerCase();
  const hit = (...s: (string | undefined)[]) => !needle || s.some((x) => (x ?? '').toLowerCase().includes(needle));
  const rows = map.rows.filter((r) => hit(r.channel.title, r.channel.nickname, ...r.schemas.map((t) => t.title), ...r.sources.map((m) => m.title)));
  const looseSchemas = map.looseSchemas.filter((t) => hit(t.title, t.nickname));
  const looseSources = map.looseSources.filter((m) => hit(m.title, m.nickname));

  return (
    <div className="flex flex-col gap-5">
      <ScreenHeader title="Data" backTo="/channels" />
      <p className="m-0 -mt-3 text-[13px] text-black/55 dark:text-white/55">Your channels, the schemas they use and the sources that feed them.</p>
      <div className="relative">
        <Search size={15} aria-hidden className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search channels, schemas and sources"
          aria-label="Search data"
          className="w-full h-10 rounded-xl border border-black/10 dark:border-white/12 bg-transparent pl-9 pr-3 text-[14px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
        />
      </div>

      {map.rows.length === 0 && map.looseSchemas.length === 0 && map.looseSources.length === 0 && (
        <EmptyState icon={LayoutGrid} title="Nothing here yet" subtitle="Ask Zmist for a channel, for example “Compare Apple and Microsoft revenue from sec.gov”, and its schema and source appear here." />
      )}

      <ul className="m-0 p-0 list-none flex flex-col gap-3">
        {rows.map(({ channel, schemas, sources }) => (
          <li key={channel.id} className="rounded-xl border border-black/10 dark:border-white/10 p-4">
            <div className="flex items-center gap-2">
              <LayoutGrid size={16} aria-hidden className="opacity-60 shrink-0" />
              <Link to={`/channels/${encodeURIComponent(channel.nickname)}`} className="text-[15px] font-medium hover:underline truncate">
                {channel.title}
              </Link>
              <span className="text-[12px] font-mono text-black/45 dark:text-white/45 truncate">{channel.nickname}</span>
              <span className="ml-auto text-[12px] text-black/55 dark:text-white/55 shrink-0">{channel.isPublic ? 'Public' : 'Private'}</span>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <section aria-label={`Schemas of ${channel.title}`}>
                <h3 className="m-0 mb-1 flex items-center gap-1.5 text-[12px] font-medium text-black/55 dark:text-white/55">
                  <Layers size={13} aria-hidden /> Schemas ({schemas.length})
                </h3>
                {schemas.length === 0 ? (
                  <p className="m-0 text-[13px] text-black/45 dark:text-white/45">No schema attached.</p>
                ) : (
                  <ul className="m-0 p-0 list-none text-[13px] space-y-0.5">
                    {schemas.map((t) => (
                      <li key={t.id}>
                        <Link to={`/templates/${encodeURIComponent(t.nickname)}`} className="hover:underline">
                          {t.title}
                        </Link>
                        <span className="text-black/45 dark:text-white/45"> · {t.signals.length} signal{t.signals.length === 1 ? '' : 's'}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section aria-label={`Sources of ${channel.title}`}>
                <h3 className="m-0 mb-1 flex items-center gap-1.5 text-[12px] font-medium text-black/55 dark:text-white/55">
                  <Cable size={13} aria-hidden /> Sources ({sources.length})
                </h3>
                {sources.length === 0 ? (
                  <p className="m-0 text-[13px] text-black/45 dark:text-white/45">No source feeds this channel - its data was added by hand or is a one-off.</p>
                ) : (
                  <ul className="m-0 p-0 list-none text-[13px] space-y-0.5">
                    {sources.map((m) => (
                      <li key={m.id} className="flex items-center gap-1.5">
                        <Link to={`/integrations/${encodeURIComponent(m.nickname)}`} className="hover:underline truncate">
                          {m.title}
                        </Link>
                        <span className="text-black/45 dark:text-white/45 shrink-0">· {sourceKind(m)}</span>
                        {m.enabled === false && (
                          <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 shrink-0">
                            <PauseCircle size={12} aria-hidden /> Paused
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </li>
        ))}
      </ul>

      {(looseSchemas.length > 0 || looseSources.length > 0) && (
        <section aria-label="Not connected to a channel" className="rounded-xl border border-dashed border-black/15 dark:border-white/15 p-4">
          <h2 className="m-0 text-[14px] font-medium">Not connected to a channel</h2>
          <div className="mt-2 grid gap-3 sm:grid-cols-2 text-[13px]">
            <ul className="m-0 p-0 list-none space-y-0.5">
              {looseSchemas.map((t) => (
                <li key={t.id}>
                  <Layers size={12} aria-hidden className="inline mr-1 opacity-60" />
                  <Link to={`/templates/${encodeURIComponent(t.nickname)}`} className="hover:underline">
                    {t.title}
                  </Link>
                </li>
              ))}
            </ul>
            <ul className="m-0 p-0 list-none space-y-0.5">
              {looseSources.map((m) => (
                <li key={m.id}>
                  <Cable size={12} aria-hidden className="inline mr-1 opacity-60" />
                  <Link to={`/integrations/${encodeURIComponent(m.nickname)}`} className="hover:underline">
                    {m.title}
                  </Link>
                  <span className="text-black/45 dark:text-white/45"> · {sourceKind(m)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}
